/**
 * ChatContext — broadcasts the currently-selected KV (id + name) so the
 * floating ChatBubble can include it in every request to /api/chat/message.
 *
 * Usage:
 *   1. Wrap the authenticated app inside <ChatProvider> (already done in UserLayout).
 *   2. In any page that selects a KV, call `setCurrentKv({ id, name })`
 *      when the selection changes. Call `setCurrentKv(null)` on reset.
 *
 * The provider intentionally stores ONLY id+name (no ruleset) — the full
 * deep context is loaded server-side via _resolve_active_kv() to keep this
 * client state lightweight and avoid stale duplication.
 */
import React, { createContext, useContext, useState, useCallback } from "react";

const ChatContext = createContext({
  currentKv: null,
  setCurrentKv: () => {},
});

export function ChatProvider({ children }) {
  const [currentKv, setCurrentKvState] = useState(null);

  const setCurrentKv = useCallback((next) => {
    if (next && (typeof next !== "object" || !next.id)) {
      setCurrentKvState(null);
      return;
    }
    setCurrentKvState(next || null);
  }, []);

  return (
    <ChatContext.Provider value={{ currentKv, setCurrentKv }}>
      {children}
    </ChatContext.Provider>
  );
}

export function useChatContext() {
  return useContext(ChatContext);
}
